import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import profileService from '../services/profileService';
import ProfilePicture from '../components/ProfilePicture';
import ScrollReveal, { RevealUp, RevealLeft, RevealRight } from '../components/ScrollReveal';

const Profile = () => {
  const { user, updateUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('personal');
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: '',
    dateOfBirth: '',
    gender: '',
    occupation: '',
    bio: '',
    preferences: {
      newsletter: true,
      notifications: true,
      language: 'hindi'
    }
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const profileData = await profileService.getProfile();
      setProfile(profileData);
      setFormData({
        name: profileData.name || '',
        phone: profileData.phone || '',
        address: profileData.address || '',
        dateOfBirth: profileData.dateOfBirth ? profileData.dateOfBirth.split('T')[0] : '',
        gender: profileData.gender || '',
        occupation: profileData.occupation || '',
        bio: profileData.bio || '',
        preferences: {
          newsletter: profileData.preferences?.newsletter ?? true,
          notifications: profileData.preferences?.notifications ?? true,
          language: profileData.preferences?.language || 'hindi'
        }
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith('preferences.')) {
      const prefKey = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        preferences: {
          ...prev.preferences,
          [prefKey]: type === 'checkbox' ? checked : value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const updatedProfile = await profileService.updateProfile(formData);
      setProfile(updatedProfile.user);
      updateUser(updatedProfile.user);
      alert('प्रोफ़ाइल सफलतापूर्वक अपडेट हो गई!');
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('प्रोफ़ाइल अपडेट करने में त्रुटि हुई');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert('नया पासवर्ड और पुष्टि पासवर्ड मेल नहीं खाते');
      return;
    }

    setSaving(true);
    
    try {
      await profileService.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      alert('पासवर्ड सफलतापूर्वक बदल दिया गया!');
    } catch (error) {
      console.error('Error changing password:', error);
      alert(error.message || 'पासवर्ड बदलने में त्रुटि हुई');
    } finally {
      setSaving(false);
    }
  };

  const handleProfilePictureUpload = async (file) => {
    try {
      const result = await profileService.uploadProfilePicture(file);
      setProfile(prev => ({ ...prev, profilePicture: result.profilePicture }));
      updateUser({ ...user, profilePicture: result.profilePicture });
    } catch (error) {
      throw error;
    }
  };

  const handleProfilePictureRemove = async () => {
    try {
      await profileService.removeProfilePicture();
      setProfile(prev => ({ ...prev, profilePicture: null }));
      updateUser({ ...user, profilePicture: null });
    } catch (error) {
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-temple-bg via-temple-bg-soft to-temple-bg-warm flex items-center justify-center">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-4xl text-temple-primary mb-4"></i>
          <p className="text-temple-text">प्रोफ़ाइल लोड हो रही है...</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'personal', name: 'व्यक्तिगत जानकारी', icon: 'fas fa-user' },
    { id: 'security', name: 'सुरक्षा', icon: 'fas fa-shield-alt' },
    { id: 'preferences', name: 'प्राथमिकताएं', icon: 'fas fa-cog' }
  ];

  return (
    <ScrollReveal>
      <div className="min-h-screen bg-gradient-to-br from-temple-bg via-temple-bg-soft to-temple-bg-warm py-8">
        <div className="max-w-3xl mx-auto px-4 sm:px-5">
          {/* Header */}
          <RevealUp className="text-center mb-6">
            <h1 className="text-3xl font-serif font-bold text-temple-text mb-2">
              मेरी प्रोफ़ाइल
            </h1>
            <p className="text-temple-text-muted text-sm">
              अपनी व्यक्तिगत जानकारी और सेटिंग्स प्रबंधित करें
            </p>
          </RevealUp>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* Profile Picture & Basic Info */}
            <RevealLeft className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-temple p-5 border border-temple-primary/10">
                <div className="text-center">
                  <ProfilePicture
                    profilePicture={profile?.profilePicture}
                    name={profile?.name}
                    onUpload={handleProfilePictureUpload}
                    onRemove={handleProfilePictureRemove}
                    size="xlarge"
                  />
                  
                  <div className="mt-6">
                    <h2 className="text-xl font-serif font-bold text-temple-text">
                      {profile?.name}
                    </h2>
                    <p className="text-temple-text-muted">{profile?.email}</p>
                    {profile?.role && (
                      <span className="inline-block mt-2 px-3 py-1 bg-temple-primary/10 text-temple-primary text-sm rounded-full">
                        {profile.role === 'admin' ? 'व्यवस्थापक' : 'सदस्य'}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </RevealLeft>

            {/* Main Content */}
            <RevealRight className="lg:col-span-2">
              <div className="bg-white rounded-3xl shadow-temple-lg border border-temple-primary/10 overflow-hidden">
                {/* Tabs */}
                <div className="border-b border-temple-primary/10">
                  <nav className="flex overflow-x-auto scrollbar-hide space-x-4 px-4">
                    {tabs.map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`py-3 px-1 border-b-2 font-medium text-xs whitespace-nowrap transition-colors flex-shrink-0 ${
                          activeTab === tab.id
                            ? 'border-temple-primary text-temple-primary'
                            : 'border-transparent text-temple-text-muted hover:text-temple-text hover:border-temple-primary/30'
                        }`}
                      >
                        <i className={`${tab.icon} mr-1.5`}></i>
                        {tab.name}
                      </button>
                    ))}
                  </nav>
                </div>

                {/* Tab Content */}
                <div className="p-6">
                  {activeTab === 'personal' && (
                    <form onSubmit={handleProfileUpdate} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-temple-text mb-2">
                            पूरा नाम *
                          </label>
                          <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            required
                            className="w-full px-4 py-3 border border-temple-primary/20 rounded-xl focus:ring-2 focus:ring-temple-primary focus:border-transparent"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-temple-text mb-2">
                            फ़ोन नंबर
                          </label>
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            pattern="[0-9]{10}"
                            className="w-full px-4 py-3 border border-temple-primary/20 rounded-xl focus:ring-2 focus:ring-temple-primary focus:border-transparent"
                          />
                        </div>

                        <div>
                          <label className="block  font-medium text-temple-text mb-2">
                            जन्म तिथि
                          </label>
                          <input
                            type="date"
                            name="dateOfBirth"
                            value={formData.dateOfBirth}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-temple-primary/20 rounded-xl focus:ring-2 focus:ring-temple-primary focus:border-transparent"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-temple-text mb-2">
                            लिंग
                          </label>
                          <select
                            name="gender"
                            value={formData.gender}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-temple-primary/20 rounded-xl focus:ring-2 focus:ring-temple-primary focus:border-transparent"
                          >
                            <option value="">चुनें</option>
                            <option value="male">पुरुष</option>
                            <option value="female">महिला</option>
                            <option value="other">अन्य</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-temple-text mb-2">
                            व्यवसाय
                          </label>
                          <input
                            type="text"
                            name="occupation"
                            value={formData.occupation}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-temple-primary/20 rounded-xl focus:ring-2 focus:ring-temple-primary focus:border-transparent"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-temple-text mb-2">
                          पता
                        </label>
                        <textarea
                          name="address"
                          value={formData.address}
                          onChange={handleInputChange}
                          rows={3}
                          className="w-full px-4 py-3 border border-temple-primary/20 rounded-xl focus:ring-2 focus:ring-temple-primary focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-temple-text mb-2">
                          बायो
                        </label>
                        <textarea
                          name="bio"
                          value={formData.bio}
                          onChange={handleInputChange}
                          rows={4}
                          placeholder="अपने बारे में कुछ बताएं..."
                          className="w-full px-4 py-3 border border-temple-primary/20 rounded-xl focus:ring-2 focus:ring-temple-primary focus:border-transparent"
                        />
                      </div>

                      <div className="flex justify-end">
                        <button
                          type="submit"
                          disabled={saving}
                          className="px-8 py-3 bg-temple-primary text-white rounded-xl hover:bg-temple-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {saving ? (
                            <>
                              <i className="fas fa-spinner fa-spin mr-2"></i>
                              सेव हो रहा है...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-save mr-2"></i>
                              सेव करें
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  )}

                  {activeTab === 'security' && (
                    <form onSubmit={handlePasswordUpdate} className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-temple-text mb-2">
                          वर्तमान पासवर्ड *
                        </label>
                        <input
                          type="password"
                          name="currentPassword"
                          value={passwordData.currentPassword}
                          onChange={handlePasswordChange}
                          required
                          className="w-full px-4 py-3 border border-temple-primary/20 rounded-xl focus:ring-2 focus:ring-temple-primary focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-temple-text mb-2">
                          नया पासवर्ड *
                        </label>
                        <input
                          type="password"
                          name="newPassword"
                          value={passwordData.newPassword}
                          onChange={handlePasswordChange}
                          required
                          minLength={6}
                          className="w-full px-4 py-3 border border-temple-primary/20 rounded-xl focus:ring-2 focus:ring-temple-primary focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-temple-text mb-2">
                          नया पासवर्ड पुष्टि *
                        </label>
                        <input
                          type="password"
                          name="confirmPassword"
                          value={passwordData.confirmPassword}
                          onChange={handlePasswordChange}
                          required
                          minLength={6}
                          className="w-full px-4 py-3 border border-temple-primary/20 rounded-xl focus:ring-2 focus:ring-temple-primary focus:border-transparent"
                        />
                      </div>

                      <div className="flex justify-end">
                        <button
                          type="submit"
                          disabled={saving}
                          className="px-8 py-3 bg-temple-primary text-white rounded-xl hover:bg-temple-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {saving ? (
                            <>
                              <i className="fas fa-spinner fa-spin mr-2"></i>
                              अपडेट हो रहा है...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-key mr-2"></i>
                              पासवर्ड बदलें
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  )}

                  {activeTab === 'preferences' && (
                    <form onSubmit={handleProfileUpdate} className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-temple-text mb-2">
                          भाषा
                        </label>
                        <select
                          name="preferences.language"
                          value={formData.preferences.language}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-temple-primary/20 rounded-xl focus:ring-2 focus:ring-temple-primary focus:border-transparent"
                        >
                          <option value="hindi">हिंदी</option>
                          <option value="english">English</option>
                        </select>
                      </div>

                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-sm font-medium text-temple-text">न्यूज़लेटर</h3>
                            <p className="text-sm text-temple-text-muted">मंदिर की नवीनतम जानकारी प्राप्त करें</p>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              name="preferences.newsletter"
                              checked={formData.preferences.newsletter}
                              onChange={handleInputChange}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-temple-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-temple-primary"></div>
                          </label>
                        </div>

                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-sm font-medium text-temple-text">सूचनाएं</h3>
                            <p className="text-sm text-temple-text-muted">महत्वपूर्ण अपडेट और घटनाओं की सूचना</p>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              name="preferences.notifications"
                              checked={formData.preferences.notifications}
                              onChange={handleInputChange}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-temple-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-temple-primary"></div>
                          </label>
                        </div>
                      </div>

                      <div className="flex justify-end">
                        <button
                          type="submit"
                          disabled={saving}
                          className="px-8 py-3 bg-temple-primary text-white rounded-xl hover:bg-temple-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {saving ? (
                            <>
                              <i className="fas fa-spinner fa-spin mr-2"></i>
                              सेव हो रहा है...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-save mr-2"></i>
                              सेव करें
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </RevealRight>
          </div>
        </div>
      </div>
    </ScrollReveal>
  );
};

export default Profile;